"use client"

import { lazy, useMemo } from "react"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"

import { isAllBooksId } from "@/lib/all-books"
import { DOCS_URL } from "@/lib/docs"
import { useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { useSupportChatSettings } from "@/hooks/use-show-intercom"
import { ClientForm } from "@/components/clients/use-create-client-dialog"
import { ContactForm } from "@/components/contacts/use-create-contact-dialog"
import { ProspectForm } from "@/components/prospects/use-create-prospect-dialog"

import { CommandPalette } from "../components/dialogs/command-palette"

const CreateTaskDialog = lazy(() => import("@/components/tasks/create/dialog"))
const ComposeEmail = lazy(() => import("@/components/email/compose").then((mod) => ({ default: mod.ComposeEmail })))
const NoteDialog = lazy(() => import("@/components/notes/note-dialog").then((mod) => ({ default: mod.NoteDialog })))

export interface ActionHandler {
  (): void
}

export interface GlobalAction {
  id: string
  handler: ActionHandler
}

export function useGlobalActions() {
  const { openDialog, closeDialog, isOpen } = useGlobalDialogStore()
  const { push } = useLoadingRouter({ bookScoped: false })
  const bookId = useBookId()
  const { redirectToDocs } = useSupportChatSettings()

  return useMemo(() => {
    const actions: Record<string, ActionHandler> = {
      "open-command-palette": () => {
        if (isOpen) {
          closeDialog()
          return
        }

        openDialog({
          bookId,
          variant: "custom",
          content: <CommandPalette />,
          dialogContentClasses: "max-w-[840px] h-[420px]",
        })
      },
      "show-help-chat": () => {
        if (redirectToDocs) {
          window.open(DOCS_URL, "_blank", "noopener,noreferrer")
          return
        }

        window.dispatchEvent(new CustomEvent("toggle-intercom-chat", { detail: {} }))
      },
      "create-task": () => {
        const addTaskButton = document.querySelector('[data-action="add-task"]') as HTMLElement | null
        if (addTaskButton) {
          addTaskButton.click()
          return
        }

        openDialog({ bookId, variant: "custom", content: <CreateTaskDialog /> })
      },
      "create-client": () => {
        openDialog({
          bookId,
          variant: "custom",
          content: <ClientForm />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
      "create-prospect": () => {
        openDialog({
          bookId,
          variant: "custom",
          content: <ProspectForm />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
      "create-contact": () => {
        openDialog({
          bookId,
          variant: "custom",
          content: <ContactForm />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
      "compose-email": () => {
        const sendEmailButton = document.querySelector('[data-action="send-email"]') as HTMLElement | null
        if (sendEmailButton) {
          sendEmailButton.click()
          return
        }

        openDialog({
          bookId,
          variant: "custom",
          content: <ComposeEmail />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
          preserveOnNavigation: true,
        })
      },
      "create-note": () => {
        const directButton = document.querySelector('[data-action="add-note"]') as HTMLElement | null
        if (directButton) {
          directButton.click()
          return
        }

        const moreActionsButton = document.querySelector('[data-action="more-actions"]') as HTMLElement | null
        if (moreActionsButton) {
          const style = document.createElement("style")
          style.textContent = `
            [role="menu"][data-radix-menu-content] {
              opacity: 0 !important;
              pointer-events: auto !important;
            }
          `
          document.head.appendChild(style)

          const event = new MouseEvent("pointerdown", {
            view: window,
            bubbles: true,
            cancelable: true,
            button: 0,
          })
          moreActionsButton.dispatchEvent(event)
          moreActionsButton.click()

          let attempts = 0
          const maxAttempts = 10
          const checkInterval = setInterval(() => {
            attempts++
            const addNoteButton = document.querySelector('[data-action="add-note"]') as HTMLElement | null

            if (addNoteButton) {
              clearInterval(checkInterval)
              addNoteButton.click()
              setTimeout(() => {
                style.remove()
              }, 300)
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval)
              style.remove()
              openDialog({
                bookId,
                variant: "custom",
                content: <NoteDialog noteId="" dialogId={`note-draft-${Date.now()}`} />,
                dialogContentClasses: "max-w-2xl",
              })
            }
          }, 50)
          return
        }

        openDialog({
          bookId,
          variant: "custom",
          content: <NoteDialog noteId="" dialogId={`note-draft-${Date.now()}`} />,
          dialogContentClasses: "max-w-2xl",
        })
      },
      "go-to-tasks": () => {
        push(`/${bookId}/tasks`)
      },
      "go-to-home": () => {
        push(`/${bookId}/dashboard`)
      },
      "go-to-book-settings": () => {
        push(`/settings/company?bookId=${bookId}`)
      },
      "go-to-clients": () => {
        push(`/${bookId}/clients`)
      },
      "go-to-prospects": () => {
        push(`/${bookId}/prospects`)
      },
      "go-to-contacts": () => {
        push(`/${bookId}/contacts`)
      },
      "go-to-opportunities": () => {
        push(`/${bookId}/opportunities`)
      },
      "go-to-projects": () => {
        push(`/${bookId}/projects`)
      },
      "go-to-meetings": () => {
        push(`/${bookId}/meetings`)
      },
      "go-to-sequences": () => {
        push(`/${bookId}/sequences`)
      },
      "go-to-book-chat": () => {
        if (!isAllBooksId(bookId)) {
          push(`/${bookId}/dashboard?tab=chat`)
        }
      },
    }

    return actions
  }, [openDialog, push, bookId, isOpen, closeDialog, redirectToDocs])
}
